// API client del módulo Bazares y Expositores.
// Backend: apps/exhibitions de mcp_django, montado en:
//   /api/v1/exhibitions/*          (público: fechas, sedes)
//   /api/v1/admin/exhibitions/*    (admin: ventas, stands, padrón, deudas, captura)
//
// La separación público/admin vive en el TRANSPORTE (routers distintos, el admin
// con auth). Aquí solo consumimos el lado admin, que es el del dashboard.

import { ensureFreshToken } from '@/lib/tokenRefresh';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/exhibitions';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class BazaresError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'BazaresError';
    this.status = status;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}${path}`, {
    ...init,
    headers: { ...(await authHeaders()), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || body?.message || JSON.stringify(body).slice(0, 200);
    } catch {
      /* respuesta sin JSON — se queda el status */
    }
    throw new BazaresError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── Tipos (espejo de los Schema del router) ───────────────────────────

export type EditionSales = {
  edition_id: number;
  name: string;
  venue: string;
  start_date: string;
  capacity: number | null;
  stands_tomados: number;
  cupo_restante: number | null;
  cuota_lista: string;
  esperado: string;
  cobrado: string;
  por_cobrar: string;
  currency: string;
};

export type StandRow = {
  assignment_id: number;
  stand_code: string;
  exhibitor_id: number;
  exhibitor: string;
  vende: string;
  category: string;
  logo_url: string;
  phone: string;
  cuota: string;
  pagado: string;
  saldo: string;
  status: string;
};

export type ExhibitorCard = {
  exhibitor_id: number;
  name: string;
  vende: string;
  category: string;
  logo_url: string;
  phone: string;
  email: string;
  status: string;
  ediciones_participadas: number;
  ediciones_asistidas: number;
  pagado_total: string;
  saldo_total: string;
};

export type PagoDetalle = {
  fecha: string;       // ISO datetime
  monto: string;
  metodo: string;      // stripe | transfer | cash…
  status: string;
  referencia: string;
};

export type DebtRow = {
  exhibitor: string;
  phone: string;
  edition: string;
  start_date: string;
  cuota: string;
  pagado: string;
  saldo: string;
  assignment_id: number;
  status: string;
  /** Cada pago con fecha, método y referencia — "cuándo se hizo y todo". */
  pagos: PagoDetalle[];
};

export type EnvioLigaOut = {
  assignment_id: number;
  expositor: string;
  modo: 'anticipo' | 'saldo' | 'completo';
  monto: string;
  liga: string;
  envio: { enviado: boolean; error?: string | null; desde?: string; entrega?: unknown };
};

export type Solicitud = {
  id: string;
  nombre: string;
  telefono: string;
  email: string;
  /** Acumula lo que la persona ha contado en distintas conversaciones. */
  mensaje: string;
  origen: string;
  estatus: string;
  creado: string;
  actualizado: string;
  /** Curaduría: "" (sin decidir) | "aprobado" | "rechazado". Vive en
   *  `Lead.extra` del backend, no en una columna: la decisión es del negocio
   *  de bazares y no obliga a migrar el modelo de leads compartido. */
  curacion: string;
  curacion_motivo: string;
  curacion_por: string;
  /** Si ya se le avisó a la persona. El envío lo autoriza la administradora. */
  curacion_avisado: boolean;
  /** Para APROBADOS: con qué cobrarle, ya resuelto por el backend. */
  curacion_edicion: string;
  curacion_cuota: string;
  curacion_anticipo: string;
  /** Giro ya limpio (el backend descarta el mensaje completo como giro). */
  curacion_giro?: string;
  /** Cuando está aprobada para VARIAS ediciones: sus nombres. */
  curacion_ediciones?: string[];
  curacion_sedes_excluidas?: string[];
  curacion_indicacion?: string;
  /** Lo que pidió DESPUÉS de tu decisión (rack, otro giro, otra fecha) y no
   *  has revisado. Se limpia con «Ya lo revisé» o al volver a decidir. */
  curacion_novedades?: { en: string; texto: string }[];
};

/** Etapa de una persona con una edición. La CALCULA el backend de la decisión,
 *  el apartado y los pagos; aquí solo se pinta. */
export type Etapa = 'interesado' | 'aprobado' | 'informado' | 'liga' | 'anticipo' | 'pagado' | 'no_entra';

export type Interesado = {
  lead_id: string | null;
  nombre: string;
  telefono: string;
  giro: string;
  etapa: Etapa;
  etapa_texto: string;
  fuentes: string[];
  pagado: string;
  saldo: string;
};

export type InteresEdicion = {
  edition_id: number;
  edicion: string;
  sede: string;
  fecha_inicio: string;
  /** Sin contar a quienes «no entran». */
  total: number;
  por_etapa: Record<Etapa, number>;
  interesados: Interesado[];
};

export type DecisionOut = {
  lead_id: string;
  nombre: string;
  telefono: string;
  decision: 'aprobado' | 'rechazado';
  motivo: string;
  alternativas: Array<{
    edition_id: number; nombre: string; sede: string;
    fecha_inicio: string; fecha_fin: string | null; cuota: string;
  }>;
  siguiente_paso: string;
};

// ── Material de apoyo ─────────────────────────────────────────────────
// Fotos, videos y PDFs que el agente le comparte a la gente por WhatsApp
// (el stand, ediciones anteriores, el calendario).

export type MaterialTipo = 'imagen' | 'video' | 'pdf';

export type Material = {
  id: number;
  tipo: MaterialTipo;
  titulo: string;
  etiquetas: string[];
  cuando_usar: string;
  url: string;
  tamano: number;
  /** false = WhatsApp no lo puede adjuntar (video .mov o > 16 MB): el agente
   *  manda la liga en su lugar. */
  whatsapp_ok: boolean;
  activo: boolean;
  creado: string | null;
};

/** multipart: aquí NO va `Content-Type` — el navegador pone el boundary. */
async function subirMaterial(file: File, titulo: string, etiquetas: string, cuando_usar: string): Promise<Material> {
  const headers = await authHeaders();
  delete headers['Content-Type'];
  const fd = new FormData();
  fd.append('file', file);
  fd.append('titulo', titulo);
  fd.append('etiquetas', etiquetas);
  fd.append('cuando_usar', cuando_usar);
  const res = await fetch(`${DJANGO_PROXY}${BASE}/material/subir`, { method: 'POST', headers, body: fd });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || body?.message || detail;
    } catch {
      if (res.status === 413) detail = 'El archivo pesa demasiado.';
    }
    throw new BazaresError(detail, res.status);
  }
  return res.json();
}

// ── Lectura ───────────────────────────────────────────────────────────

export const bazaresApi = {
  /** `desde`/`hasta` en YYYY-MM-DD. Sin `desde`, trae el histórico completo —
   *  que en Ventas por bazar parece un acumulado millonario y no lo es. */
  sales: (limit = 100, desde = '', hasta = '') =>
    req<EditionSales[]>(
      `/sales?limit=${limit}${desde ? `&desde=${desde}` : ''}${hasta ? `&hasta=${hasta}` : ''}`,
    ),

  stands: (editionId: number) => req<StandRow[]>(`/editions/${editionId}/stands`),

  exhibitors: (q = '', limit = 100) =>
    req<ExhibitorCard[]>(`/exhibitors?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),

  debts: (limit = 100) => req<DebtRow[]>(`/debts?limit=${limit}`),

  /** La administradora le manda al expositor su liga por WhatsApp, vía el
   *  agente. `saldo` = lo que falta; `anticipo` = el % de apartado. El backend
   *  responde 409 si ya pagó lo que la liga cobraría. */
  enviarLiga: (assignment_id: number, modo: 'saldo' | 'anticipo' | 'completo' = 'saldo') =>
    req<EnvioLigaOut>('/assignments/enviar-liga', {
      method: 'POST',
      body: JSON.stringify({ assignment_id, modo }),
    }),

  /** Gasto de una edición (renta, montaje, difusión…). */
  crearGasto: (body: { edition_id: number; amount: number; concept: string; cost_type?: string; notes?: string }) =>
    req<{ id: number }>('/costs', { method: 'POST', body: JSON.stringify(body) }),

  /** Prospectos que pidieron participar. Vienen de `sales.Lead`, que es donde
   *  el agente los guarda — una sola fuente, sin copiarlos a otra tabla. */
  solicitudes: (q = '', limit = 50) =>
    req<Solicitud[]>(`/solicitudes?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),

  /** Aprobar o rechazar. NO le escribe a la persona: solo asienta la decisión.
   *  Al rechazar devuelve `alternativas` — las fechas donde ese giro sí cabe —
   *  para que el rechazo no sea un callejón. */
  decidirSolicitud: (body: {
    lead_id: string;
    decision: 'aprobar' | 'rechazar';
    motivo?: string;
    giro?: string;
    edition_id?: number;
    /** Varias ediciones, o 'cualquiera' para no amarrarla a ninguna fecha. */
    ediciones?: number[] | 'cualquiera';
  }) => req<DecisionOut>('/solicitudes/decision', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /** «Ya lo revisé»: lo que pidió alguien ya aprobado deja de estar pendiente. */
  marcarNovedadesVistas: (lead_id: string) =>
    req<{ lead_id: string; marcadas: number }>('/solicitudes/novedades-vistas', {
      method: 'POST',
      body: JSON.stringify({ lead_id }),
    }),

  /** Aprobar o no lo que pidió (rack, mesa extra…) y mandarle `respuesta` por
   *  WhatsApp. No cambia la aprobación de su lugar. `envio.enviado=false` si no
   *  le llegó (p. ej. lleva más de 24 h sin escribir). */
  responderNovedades: (lead_id: string, decision: 'aprobar' | 'rechazar', respuesta: string) =>
    req<{ lead_id: string; decision: string; envio: { enviado: boolean; error?: string | null } }>(
      '/solicitudes/novedades-responder',
      { method: 'POST', body: JSON.stringify({ lead_id, decision, respuesta }) },
    ),

  /** Interesados por edición, cada uno con su etapa (de interesado a pagado). */
  interes: (incluirPasadas = false) =>
    req<InteresEdicion[]>(`/interes${incluirPasadas ? '?incluir_pasadas=true' : ''}`),

  // ── Captura ─────────────────────────────────────────────────────────
  // El backend normaliza alias en el borde (sede/venue, cuota/fee_amount…),
  // así que mandamos los nombres canónicos y él tolera variantes.

  createEdition: (data: Record<string, unknown>) =>
    req<Record<string, unknown>>('/editions', { method: 'POST', body: JSON.stringify(data) }),

  createExhibitor: (data: Record<string, unknown>) =>
    req<Record<string, unknown>>('/exhibitors', { method: 'POST', body: JSON.stringify(data) }),

  assignStand: (data: Record<string, unknown>) =>
    req<Record<string, unknown>>('/assignments', { method: 'POST', body: JSON.stringify(data) }),

  registerPayment: (data: Record<string, unknown>) =>
    req<Record<string, unknown>>('/payments', { method: 'POST', body: JSON.stringify(data) }),

  // ── Material de apoyo ──────────────────────────────────────────────
  material: () => req<Material[]>('/material'),

  subirMaterial,

  editarMaterial: (id: number, cambios: Partial<Pick<Material, 'titulo' | 'etiquetas' | 'cuando_usar'>>) =>
    req<Material>('/material/editar', { method: 'POST', body: JSON.stringify({ id, ...cambios }) }),

  /** Lo quita de lo que comparte el agente. El archivo no se borra: una liga
   *  que ya se mandó sigue abriendo. */
  archivarMaterial: (id: number) =>
    req<Material>('/material/archivar', { method: 'POST', body: JSON.stringify({ id }) }),
};
